import React, { useEffect, useState } from 'react';
import {
  Box, Paper, Typography, Chip, Stack, IconButton, Tooltip, CircularProgress, Alert,
} from '@mui/material';
import {
  CheckCircle as OkIcon,
  Error as DownIcon,
  Warning as WarnIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import PageHeader from '../components/PageHeader';
import GeneralSettings from './GeneralSettings';
import FedExSettings from '../components/FedExSettings';
import api from '../services/api';

function ServiceBadge({ status }) {
  const map = {
    active:   { color: 'success', icon: <OkIcon fontSize="small" />, label: 'Active' },
    degraded: { color: 'warning', icon: <WarnIcon fontSize="small" />, label: 'Degraded' },
    down:     { color: 'error',   icon: <DownIcon fontSize="small" />, label: 'Down' },
    unknown:  { color: 'default', icon: <WarnIcon fontSize="small" />, label: 'Unknown' },
  };
  const cfg = map[status] || map.unknown;
  return <Chip size="small" icon={cfg.icon} label={cfg.label} color={cfg.color} variant="outlined" />;
}

function SystemSettings() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  // v10.18.3: surface fetch errors instead of swallowing them. The
  // pre-fix `catch (_)` left status=null and the panel rendered every
  // service as Down/?/?? regardless of actual health.
  const [statusError, setStatusError] = useState(null);

  const refresh = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/api/admin/system-status');
      setStatus(data);
      setStatusError(null);
    } catch (e) {
      setStatusError(e.response?.data?.detail || e.message);
    } finally { setLoading(false); }
  };
  useEffect(() => { refresh(); const t = setInterval(refresh, 30000); return () => clearInterval(t); }, []);

  // Email row caption: render the actual provider when configured, so
  // a Zoho/Mailgun/SES rollout doesn't keep showing "smtp.gmail.com:587".
  const emailCaption = (() => {
    if (!status) return '—';
    if (!status.email_configured) return 'Not configured';
    const host = status.email_smtp_host || 'smtp';
    const port = status.email_smtp_port || '';
    const from = status.email_from ? ` · from ${status.email_from}` : '';
    return `${host}${port ? ':' + port : ''}${from}`;
  })();

  return (
    <Box>
      <PageHeader title="System Settings" />

      <Paper sx={{ mt: 3, p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">Services</Typography>
          <Box sx={{ flex: 1 }} />
          <Tooltip title="Refresh">
            <IconButton size="small" onClick={refresh}>
              {loading ? <CircularProgress size={18} /> : <RefreshIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
        </Box>
        {statusError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            Couldn&apos;t load system status: {statusError}
          </Alert>
        )}
        <Stack spacing={1.5}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{ minWidth: 220 }}>
              <Typography variant="body2" fontWeight={500}>Resumable upload server</Typography>
              <Typography variant="caption" color="text.secondary">tusd-uploads · /tus/files/</Typography>
            </Box>
            <ServiceBadge status={status?.tusd_uploads?.status} />
            {status?.tusd_uploads?.detail && (
              <Typography variant="caption" color="text.secondary">
                {status.tusd_uploads.detail}
              </Typography>
            )}
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{ minWidth: 220 }}>
              <Typography variant="body2" fontWeight={500}>Email (outbound notifications)</Typography>
              <Typography variant="caption" color="text.secondary">{emailCaption}</Typography>
            </Box>
            <ServiceBadge status={status?.email_configured ? 'active' : 'down'} />
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{ minWidth: 220 }}>
              <Typography variant="body2" fontWeight={500}>Storage</Typography>
              <Typography variant="caption" color="text.secondary">
                {status?.storage_used_gb ?? '?'} / {status?.storage_total_gb ?? '?'} GB used
              </Typography>
            </Box>
            <ServiceBadge status={status?.storage_available ? 'active' : 'degraded'} />
          </Box>
        </Stack>
      </Paper>

      <Paper sx={{ mt: 3, p: 3 }}>
        <GeneralSettings />
      </Paper>

      <Paper sx={{ mt: 3, p: 3 }}>
        <FedExSettings />
      </Paper>
    </Box>
  );
}

export default SystemSettings;